import { FsnNetworkSendDialog } from '@/components/orders/FsnNetworkSendDialog'
import {
  getFsnNetworkOrderEligibility,
  hasFsnNetworkCardDetails,
} from '@/components/orders/fsn-network-eligibility'
import CharacterCount from '@/components/form/CharacterCount'
import { Button } from '@/components/ui/button'
import { PosCompletionDialog } from './PosCompletionDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Can } from '@/lib/casl'
import { serverFieldErrors, useAppForm } from '@/lib/form'
import { ORDER_FIELD_MAX_LENGTHS, ORDER_FIELD_NATIVE_MAX_LENGTHS } from '@/lib/order-field-limits'
import {
  salesOrderQueries,
  useUpdateSalesOrderMutation,
} from '@/lib/services/sales-orders/sales-orders.query'
import type { SalesOrderShowData } from '@/lib/services/sales-orders/sales-orders.query'
import type { SalesOrder } from '@/lib/services/sales-orders/sales-orders.types'
import { getErrorMessage } from '@/lib/utils'
import { revalidateLogic } from '@tanstack/react-form'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2, MessageCircleHeart, Send, ShoppingCart } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { z } from 'zod/v4'

const fsnCardDetailsSchema = z
  .object({
    card_message: z.string(),
    signature: z.string(),
  })
  .refine((value) => value.card_message.trim() || value.signature.trim(), {
    message: 'Add a card message or signature.',
    path: ['card_message'],
  })

type PosFsnCardDetailsDialogProps = {
  order: SalesOrderShowData
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: (order: SalesOrder) => void
}

function PosFsnCardDetailsDialog({
  order,
  open,
  onOpenChange,
  onSaved,
}: PosFsnCardDetailsDialogProps) {
  const updateSalesOrderMutation = useUpdateSalesOrderMutation({
    organizationId: order.organization_id,
  })
  const form = useAppForm({
    defaultValues: {
      card_message: order.card_message ?? '',
      signature: order.signature ?? '',
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onDynamic: fsnCardDetailsSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          const updatedOrder = await updateSalesOrderMutation.mutateAsync({
            id: order.id,
            card_message: value.card_message,
            signature: value.signature,
          })

          onSaved(updatedOrder)
          return undefined
        } catch (error) {
          toast.error('Could not save card details', {
            description: getErrorMessage(error) || 'Please try again.',
          })

          return serverFieldErrors(error)
        }
      },
    },
  })

  const handleClose = () => {
    if (updateSalesOrderMutation.isPending) {
      return
    }

    form.reset({
      card_message: order.card_message ?? '',
      signature: order.signature ?? '',
    })
    onOpenChange(false)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircleHeart className="size-5" />
            Add card details
          </DialogTitle>
          <DialogDescription>
            Flower Shop Network needs a card message or signature before this order can be sent.
          </DialogDescription>
        </DialogHeader>

        <form.AppForm>
          <form.Form>
            <div className="grid gap-4">
              <form.AppField name="card_message">
                {(field) => (
                  <div className="grid gap-1">
                    <field.TextareaInput
                      id={`pos-fsn-card-message-${order.id}`}
                      label="Card message"
                      placeholder="What should be written on the enclosure card?"
                      maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.card_message}
                      characterMaxLength={ORDER_FIELD_MAX_LENGTHS.card_message}
                      rows={4}
                    />
                    <CharacterCount
                      value={field.state.value}
                      maxLength={ORDER_FIELD_MAX_LENGTHS.card_message}
                    />
                  </div>
                )}
              </form.AppField>

              <form.AppField name="signature">
                {(field) => (
                  <div className="grid gap-1">
                    <field.TextInput
                      id={`pos-fsn-signature-${order.id}`}
                      label="Signature"
                      placeholder="Who is this card from?"
                      maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.signature}
                      characterMaxLength={ORDER_FIELD_MAX_LENGTHS.signature}
                    />
                    <CharacterCount
                      value={field.state.value}
                      maxLength={ORDER_FIELD_MAX_LENGTHS.signature}
                    />
                  </div>
                )}
              </form.AppField>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={updateSalesOrderMutation.isPending}
              >
                Cancel
              </Button>
              <form.SubmitButton className="w-auto" mutation={updateSalesOrderMutation}>
                Save & Continue
              </form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  )
}

type PosOrderCompletionFlowProps = {
  salesOrderId: number | null
  organizationId: number | null
  canSendToFsn?: boolean
  requiresFsnCardDetails?: boolean
  isViewingOrder?: boolean
  onStartNewOrder: () => void
  onViewOrder: () => void
}

export function PosOrderCompletionFlow({
  salesOrderId,
  organizationId,
  canSendToFsn = false,
  requiresFsnCardDetails = false,
  isViewingOrder = false,
  onStartNewOrder,
  onViewOrder,
}: PosOrderCompletionFlowProps) {
  const queryClient = useQueryClient()
  const [fsnOrder, setFsnOrder] = useState<SalesOrderShowData | null>(null)
  const [isPreparingFsnOrder, setIsPreparingFsnOrder] = useState(false)
  const [isCardDetailsDialogOpen, setIsCardDetailsDialogOpen] = useState(false)
  const [isFsnDialogOpen, setIsFsnDialogOpen] = useState(false)
  const [wasSentToFsn, setWasSentToFsn] = useState(false)
  const isBusy = isViewingOrder || isPreparingFsnOrder

  async function handleOpenFsnDialog(canManageDetails: boolean) {
    if (!salesOrderId || !organizationId) {
      return
    }

    setIsPreparingFsnOrder(true)

    try {
      const order = await queryClient.fetchQuery(
        salesOrderQueries.show({ organizationId, salesOrderId })
      )
      const eligibility = getFsnNetworkOrderEligibility(order, {
        allowMissingCardDetails: true,
      })

      if (!eligibility.isEligible) {
        toast.error('Order is not ready for Flower Shop Network', {
          description: eligibility.errors[0] ?? 'Review the order details and try again.',
        })
        return
      }

      setFsnOrder(order)
      if (hasFsnNetworkCardDetails(order)) {
        setIsFsnDialogOpen(true)
      } else if (!canManageDetails) {
        toast.error('Order is missing card details', {
          description: 'Ask someone with permission to edit order details to add them first.',
        })
      } else {
        setIsCardDetailsDialogOpen(true)
      }
    } catch (error) {
      toast.error('Could not prepare Flower Shop Network order', {
        description: getErrorMessage(error) || 'Please try again',
      })
    } finally {
      setIsPreparingFsnOrder(false)
    }
  }

  function handleCardDetailsSaved(updatedOrder: SalesOrder) {
    const preparedOrder = { ...fsnOrder, ...updatedOrder }
    const eligibility = getFsnNetworkOrderEligibility(preparedOrder)

    if (!eligibility.isEligible) {
      toast.error('Order is not ready for Flower Shop Network', {
        description: eligibility.errors[0] ?? 'Review the order details and try again.',
      })
      return
    }

    setFsnOrder(preparedOrder)
    setIsCardDetailsDialogOpen(false)
    setIsFsnDialogOpen(true)
  }

  return (
    <>
      <PosCompletionDialog
        open={Boolean(salesOrderId) && !isCardDetailsDialogOpen && !isFsnDialogOpen}
        icon={<ShoppingCart className="h-5 w-5" />}
        title="Order Complete"
        description="This order has been placed. View it, or start a new order."
        busy={isBusy}
        onViewOrder={onViewOrder}
        start={{
          label: 'Start New Order',
          icon: <ShoppingCart className="h-4 w-4" />,
          onClick: onStartNewOrder,
        }}
      >
        <Can I="view" a="sales-orders" passThrough>
          {(canViewSalesOrders) => (
            <Can I="edit" a="sales-orders" field="fulfillment" passThrough>
              {(canManageFulfillment) => (
                <Can I="edit" a="sales-orders" field="details" passThrough>
                  {(canManageDetails) =>
                    canSendToFsn &&
                    canViewSalesOrders &&
                    canManageFulfillment &&
                    (!requiresFsnCardDetails || canManageDetails) &&
                    !wasSentToFsn ? (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => handleOpenFsnDialog(canManageDetails)}
                        disabled={isBusy}
                        className="gap-2"
                        data-pos-order-complete-action="send-to-fsn"
                      >
                        {isPreparingFsnOrder ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                        {isPreparingFsnOrder ? 'Preparing...' : 'Send to FSN'}
                      </Button>
                    ) : null
                  }
                </Can>
              )}
            </Can>
          )}
        </Can>
      </PosCompletionDialog>

      {fsnOrder ? (
        <PosFsnCardDetailsDialog
          key={fsnOrder.id}
          order={fsnOrder}
          open={isCardDetailsDialogOpen}
          onOpenChange={setIsCardDetailsDialogOpen}
          onSaved={handleCardDetailsSaved}
        />
      ) : null}

      {fsnOrder ? (
        <FsnNetworkSendDialog
          order={fsnOrder}
          open={isFsnDialogOpen}
          onOpenChange={setIsFsnDialogOpen}
          onSubmitted={() => setWasSentToFsn(true)}
        />
      ) : null}
    </>
  )
}
